import React, { FC, useEffect } from 'react';
import { SearchServiceQuery } from '../../../services/query';
import { Box, Button, Chip, Fade, IconButton, LinearProgress, Paper, Tooltip, Typography, alpha } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SyncIcon from '@mui/icons-material/Sync';
import LayersIcon from '@mui/icons-material/Layers';
import InventoryIcon from '@mui/icons-material/Inventory';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import StyleIcon from '@mui/icons-material/Style';
import NumbersIcon from '@mui/icons-material/Numbers';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { useTranslation } from 'react-i18next';
import { SCUICustomDataGrid } from '../../molecules';
import { SearchServiceCommand } from '../../../services/command';
import Swal from 'sweetalert2';

const InfoRow: FC<{ label: string; icon?: React.ReactNode; children: React.ReactNode }> = ({
    label,
    icon,
    children,
}) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            py: 1.1,
            borderBottom: '1px solid',
            borderColor: (theme) => alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.12 : 0.06),
            px: 0.5,
            borderRadius: 0.5,
            transition: 'background-color 0.15s ease',
            '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.05) },
            '&:last-child': { borderBottom: 'none' },
        }}
    >
        <Typography
            sx={{
                color: 'text.secondary',
                fontWeight: 600,
                fontSize: '0.77rem',
                width: '45%',
                flexShrink: 0,
                pr: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
            }}
        >
            {icon}
            {label}
        </Typography>
        <Box sx={{ flex: 1 }}>{children}</Box>
    </Box>
);

const MonoChip: FC<{ value: string | number; color?: string }> = ({ value, color = '#3730a3' }) => (
    <Chip
        size="small"
        label={value}
        sx={{
            fontFamily: 'monospace',
            fontWeight: 700,
            bgcolor: (theme) => alpha(color, theme.palette.mode === 'dark' ? 0.18 : 0.12),
            color: (theme) => (theme.palette.mode === 'dark' ? theme.palette.getContrastText(alpha(color, 0.28)) : color),
            border: (theme) => `1px solid ${alpha(color, theme.palette.mode === 'dark' ? 0.35 : 0.3)}`,
            fontSize: '0.75rem',
            height: 22,
            letterSpacing: '0.02em',
        }}
    />
);

const StockBadge: FC<{ value: number }> = ({ value }) => {
    const hasStock = value > 0;
    return (
        <Chip
            size="small"
            label={value}
            sx={{
                fontWeight: 700,
                fontSize: '0.78rem',
                height: 22,
                minWidth: 40,
                bgcolor: (theme) =>
                    hasStock ? alpha(theme.palette.success.main, 0.16) : alpha(theme.palette.text.secondary, 0.16),
                color: (theme) => (hasStock ? theme.palette.success.main : theme.palette.text.secondary),
                border: (theme) =>
                    `1px solid ${
                        hasStock ? alpha(theme.palette.success.main, 0.35) : alpha(theme.palette.text.secondary, 0.28)
                    }`,
            }}
        />
    );
};

export const SCUISearchServiceDrawer: FC<{ id: number; onClose?: any }> = ({ id, onClose }) => {
    const [loading, setLoading] = React.useState(true);
    const [stockData, setStockData] = React.useState<any>();
    const [stockProductData, setProductStockData] = React.useState<any>();
    const { t } = useTranslation();

    const productEnv = JSON.parse(import.meta.env.VITE_PRODUCT_DICTIONARY!);

    useEffect(() => {
        GetStock();
    }, [id]);

    const GetStock = async () => {
        let params: any = {};
        params.optionId = String(id);
        params.regionId = JSON.parse(localStorage.getItem('selectedCountry') || '{}')?.regionId?.toString();
        params.warehouseGroupId = productEnv[0].WarehouseGroupId;
        params.cultureCode = productEnv[0].CultureCode;
        if (id) {
            SearchServiceQuery.GetStockDetail(params).then((res: any) => {
                if (res.data) {
                    setStockData(res.data);
                    setProductStockData(res.data.products);
                    setLoading(false);
                }
            });
        }
    };

    const GetStockByProduct = async () => {
        let params: any = {
            optionId: String(id),
        };
        SearchServiceCommand.GetStockByProduct(params)
            .then((res: any) => {
                console.info(res);
                Swal.fire(res, ' ', 'success');
            })
            .catch((error: any) => {
                Swal.fire(t('general.error'), ' ', 'error');
                console.log('Hata :>> ', error);
            });
    };

    const columns = [
        {
            field: 'productId',
            headerName: t('general.productId'),
            flex: 1,
            renderCell: (params: any) => (
                <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, fontSize: '0.8rem', fontFamily: 'monospace', color: 'text.primary' }}
                >
                    {params.value}
                </Typography>
            ),
        },
        {
            field: 'size',
            headerName: t('general.size'),
            flex: 1,
            renderCell: (params: any) => (
                <Chip
                    size="small"
                    label={params.value}
                    sx={{
                        fontWeight: 700,
                        bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.12),
                        color: 'primary.main',
                        border: (theme) => `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.45 : 0.28)}`,
                        fontSize: '0.72rem',
                        height: 22,
                        minWidth: 36,
                    }}
                />
            ),
        },
        {
            field: 'stock',
            headerName: t('general.stock'),
            flex: 1,
            renderCell: (params: any) => <StockBadge value={params.value} />,
        },
    ];

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
            {/* Loading bar */}
            <Fade in={loading} unmountOnExit>
                <LinearProgress
                    sx={{
                        '& .MuiLinearProgress-bar': {
                            background: (theme) =>
                                `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                        },
                    }}
                />
            </Fade>

            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 3,
                    py: 2.25,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    background: (theme) =>
                        theme.palette.mode === 'dark'
                            ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.6)} 0%, ${alpha(
                                  theme.palette.primary.main,
                                  0.12
                              )} 100%)`
                            : 'linear-gradient(135deg, #f8faff 0%, #eef2ff 100%)',
                    flexShrink: 0,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            borderRadius: 2.5,
                            bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.1),
                            border: (theme) => `1.5px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.35 : 0.25)}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: (theme) =>
                                theme.palette.mode === 'dark'
                                    ? `0 2px 10px ${alpha(theme.palette.primary.main, 0.15)}`
                                    : '0 2px 8px rgba(99,102,241,0.15)',
                        }}
                    >
                        <LayersIcon sx={{ fontSize: 22, color: 'primary.main' }} />
                    </Box>
                    <Box>
                        <Typography
                            variant="h6"
                            sx={{ fontWeight: 700, fontSize: '1rem', color: 'text.primary', lineHeight: 1.25 }}
                        >
                            {t('searchService.stockSearch')}
                        </Typography>
                        {id && (
                            <Typography
                                variant="caption"
                                sx={{ color: 'text.disabled', fontSize: '0.7rem', fontFamily: 'monospace' }}
                            >
                                Option ID: {id}
                            </Typography>
                        )}
                    </Box>
                </Box>

                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                        color: 'text.secondary',
                        bgcolor: (theme) => alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.08 : 0.04),
                        border: '1px solid',
                        borderColor: 'divider',
                        transition: 'all 0.25s ease',
                        '&:hover': {
                            bgcolor: (theme) => alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.18 : 0.12),
                            borderColor: (theme) => alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.45 : 0.35),
                            color: 'error.main',
                            transform: 'rotate(90deg)',
                            boxShadow: (theme) => `0 2px 10px ${alpha(theme.palette.error.main, 0.22)}`,
                        },
                    }}
                >
                    <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {stockData?.modelId === 0 ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            py: 8,
                            gap: 2,
                        }}
                    >
                        <Box
                            sx={{
                                width: 64,
                                height: 64,
                                borderRadius: '50%',
                                bgcolor: (theme) => alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.18 : 0.22),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <SearchOffIcon sx={{ fontSize: 32, color: 'warning.main' }} />
                        </Box>
                        <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '1rem' }}>
                            {t('general.notFound')}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {/* ── Product Info Card ── */}
                        <Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 1.25,
                                    flexWrap: 'wrap',
                                    gap: 1,
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                    <Box
                                        sx={{
                                            width: 4,
                                            height: 20,
                                            borderRadius: 2,
                                            background: 'linear-gradient(180deg, #6366f1 0%, #4f46e5 100%)',
                                            flexShrink: 0,
                                        }}
                                    />
                                    <StyleIcon sx={{ fontSize: 17, color: 'primary.main' }} />
                                    <Typography
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: '0.88rem',
                                            color: 'text.primary',
                                            letterSpacing: '-0.01em',
                                        }}
                                    >
                                        {t('general.productInformation')}
                                    </Typography>
                                </Box>

                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<OpenInNewIcon sx={{ fontSize: '15px !important' }} />}
                                    onClick={() =>
                                        window.open(
                                            'https://www.lcw.com/model/?modelId=' +
                                                stockData?.modelId +
                                                '&optionId=' +
                                                stockData?.optionId,
                                            '_blank',
                                            'noopener,noreferrer'
                                        )
                                    }
                                    sx={{
                                        borderColor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.6 : 0.7),
                                        color: 'primary.main',
                                        fontWeight: 600,
                                        fontSize: '0.78rem',
                                        textTransform: 'none',
                                        borderRadius: 2,
                                        padding: '8px 16px !important',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            bgcolor: (theme) =>
                                                alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.08),
                                            borderColor: 'primary.main',
                                            boxShadow: (theme) => `0 2px 10px ${alpha(theme.palette.primary.main, 0.25)}`,
                                            transform: 'translateY(-1px)',
                                        },
                                    }}
                                >
                                    {t('general.goToProduct')}
                                </Button>
                            </Box>

                            <Paper
                                elevation={0}
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2.5,
                                    overflow: 'hidden',
                                    bgcolor: 'background.paper',
                                    transition: 'box-shadow 0.25s ease',
                                    '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.07)' },
                                }}
                            >
                                <Box
                                    sx={{
                                        px: 2,
                                        py: 1.1,
                                        background: (theme) =>
                                            theme.palette.mode === 'dark'
                                                ? alpha(theme.palette.text.primary, 0.04)
                                                : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    <StyleIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.07em',
                                            fontSize: '0.6rem',
                                            color: 'text.disabled',
                                        }}
                                    >
                                        {t('general.productInformation')}
                                    </Typography>
                                </Box>

                                <Box sx={{ px: 2, py: 0.5 }}>
                                    <InfoRow
                                        label={t('searchService.modelTitle')}
                                        icon={<StyleIcon sx={{ fontSize: 12 }} />}
                                    >
                                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                                            {stockData?.modelTitle}
                                        </Typography>
                                    </InfoRow>

                                    <InfoRow
                                        label={t('searchService.optionTitle')}
                                        icon={<StyleIcon sx={{ fontSize: 12 }} />}
                                    >
                                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                                            {stockData?.optionTitle}
                                        </Typography>
                                    </InfoRow>

                                    <InfoRow label={'Option Status'}>
                                        <Chip
                                            size="small"
                                            icon={
                                                stockData?.status ? (
                                                    <CheckCircleRoundedIcon sx={{ fontSize: '13px !important' }} />
                                                ) : (
                                                    <CancelRoundedIcon sx={{ fontSize: '13px !important' }} />
                                                )
                                            }
                                            label={stockData?.status ? t('general.active') : t('general.passive')}
                                            color={stockData?.status ? 'success' : 'error'}
                                            sx={{ fontWeight: 700, fontSize: '0.7rem', height: 22 }}
                                        />
                                    </InfoRow>

                                    <InfoRow
                                        label={t('searchService.modelId')}
                                        icon={<NumbersIcon sx={{ fontSize: 12 }} />}
                                    >
                                        <MonoChip value={stockData?.modelId} />
                                    </InfoRow>

                                    <InfoRow
                                        label={t('searchService.optionId')}
                                        icon={<NumbersIcon sx={{ fontSize: 12 }} />}
                                    >
                                        <MonoChip value={stockData?.optionId} />
                                    </InfoRow>
                                </Box>
                            </Paper>
                        </Box>

                        {/* ── Stock Info Section ── */}
                        {stockProductData && (
                            <Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        mb: 1.25,
                                        flexWrap: 'wrap',
                                        gap: 1,
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                        <Box
                                            sx={{
                                                width: 4,
                                                height: 20,
                                                borderRadius: 2,
                                                background: 'linear-gradient(180deg, #059669 0%, #047857 100%)',
                                                flexShrink: 0,
                                            }}
                                        />
                                        <InventoryIcon sx={{ fontSize: 17, color: 'success.main' }} />
                                        <Typography
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: '0.88rem',
                                                color: 'text.primary',
                                                letterSpacing: '-0.01em',
                                            }}
                                        >
                                            {t('general.stockInformation')}
                                        </Typography>
                                        <Chip
                                            size="small"
                                            label={stockProductData.length}
                                            sx={{
                                                height: 18,
                                                fontSize: '0.65rem',
                                                fontWeight: 700,
                                                bgcolor: (theme) => alpha(theme.palette.success.main, 0.16),
                                                color: 'success.main',
                                                border: (theme) => `1px solid ${alpha(theme.palette.success.main, 0.35)}`,
                                            }}
                                        />
                                    </Box>

                                    <Tooltip title={t('searchService.GetStock')} arrow>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<SyncIcon sx={{ fontSize: '15px !important' }} />}
                                            onClick={() => GetStockByProduct()}
                                            sx={{
                                                borderColor: (theme) =>
                                                    alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.6 : 0.7),
                                                color: 'primary.main',
                                                fontWeight: 600,
                                                fontSize: '0.78rem',
                                                textTransform: 'none',
                                                borderRadius: 2,
                                                padding: '8px 16px !important',
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    bgcolor: (theme) =>
                                                        alpha(
                                                            theme.palette.primary.main,
                                                            theme.palette.mode === 'dark' ? 0.16 : 0.08
                                                        ),
                                                    borderColor: 'primary.main',
                                                    boxShadow: (theme) =>
                                                        `0 2px 10px ${alpha(theme.palette.primary.main, 0.25)}`,
                                                    transform: 'translateY(-1px)',
                                                },
                                            }}
                                        >
                                            {t('searchService.GetStock')}
                                        </Button>
                                    </Tooltip>
                                </Box>

                                <Paper
                                    elevation={0}
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 2.5,
                                        overflow: 'hidden',
                                        transition: 'box-shadow 0.25s ease',
                                        '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.07)' },
                                    }}
                                >
                                    <SCUICustomDataGrid
                                        randomId
                                        rows={stockProductData}
                                        columns={columns}
                                        allrecord
                                        hideFooter
                                    />
                                </Paper>
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
};
